import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';

export interface AccountantCounts {
  /** Pending requests sent BY clients to the accountant (need accountant action) */
  receivedPendingCount: number;
  /** Pending invitations sent BY the accountant (awaiting client acceptance) */
  sentPendingCount: number;
  /** New events (acceptances / revocations) since last time the invitations page was visited */
  unseenCount: number;
}

/**
 * Returns pending invitation count and unseen notification count for the accountant.
 * Used by Sidebar and BottomNav to display a badge.
 */
export function useAccountantPendingCount(): AccountantCounts {
  const { user } = useAuth();
  const [counts, setCounts] = useState<AccountantCounts>({ receivedPendingCount: 0, sentPendingCount: 0, unseenCount: 0 });

  useEffect(() => {
    if (!user?.id) return;

    async function load() {
      const [linksResult, profileResult] = await Promise.all([
        supabase
          .from('accountant_clients')
          .select('status, initiated_by, accepted_at, revoked_at')
          .eq('accountant_id', user!.id),
        supabase
          .from('profiles')
          .select('invitations_seen_at')
          .eq('id', user!.id)
          .maybeSingle(),
      ]);

      const links = linksResult.data ?? [];
      const seen = (profileResult.data as unknown as Record<string, string | null> | null)?.['invitations_seen_at'] ?? null;

      const receivedPendingCount = links.filter(
        (l) => l.status === 'pending' && l.initiated_by === 'client'
      ).length;

      const sentPendingCount = links.filter(
        (l) => l.status === 'pending' && l.initiated_by === 'accountant'
      ).length;

      const unseenCount = links.filter((l) => {
        const events = [l.accepted_at, l.revoked_at].filter(Boolean) as string[];
        if (!seen) return events.length > 0;
        return events.some((e) => e > seen);
      }).length;

      setCounts({ receivedPendingCount, sentPendingCount, unseenCount });
    }

    load().catch(() => {});

    const interval = setInterval(() => load().catch(() => {}), 30_000);

    const channel = supabase
      .channel('accountant-pending-count')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'accountant_clients', filter: `accountant_id=eq.${user!.id}` },
        () => { load().catch(() => {}); }
      )
      .subscribe();

    return () => {
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  return counts;
}
